import {
  dxtToLocalServerTime,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, Grid, IconButton } from "@mui/material";
import { List as ListIcon } from "@mui/icons-material";
import { Autorenew as AutorenewIcon } from "@mui/icons-material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { LinkCellRenderer } from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { HeatStatusTag } from "./HeatStatusTag";
import { get } from "lodash-es";

type Props = {
  showContextMenu: Function;
  goToCasterDataEntry: Function;
  goToEAFDataEntry: Function;
  goToLMFDataEntry: Function;
  goToLABDataEntry: Function;
};

export const useTableData = ({
  showContextMenu,
  goToCasterDataEntry,
  goToEAFDataEntry,
  goToLMFDataEntry,
  goToLABDataEntry,
}: Props) => {
  const { state } = useUTLSettingsContext();
  const targetTTT = parseInt(get(state.settings, "EAF_TARGET_TTT", 60));

  const columns: ColDef[] = [
    {
      field: "HeatName",
      headerName: "Heat",
      flex: 1,
      minWidth: 80,
    },
    {
      headerName: "Status",
      field: "HeatStatusCode",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
            }}
          >
            <HeatStatusTag letter={params.value} showText={true} />
            {params.data?.PourbackDeclared && <AutorenewIcon />}
          </div>
        );
      },
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Grade",
      field: "GradeName",
      minWidth: 80,
    },
    {
      headerName: "Ladle",
      field: "LadleName",
      minWidth: 50,
    },
    {
      headerName: "Start",
      valueGetter: (params) => dxtToLocalServerTime(params.data.Start, "HH:mm"),
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/eaf-data-entry/${params.data.EAFProcessId}`,
        });
      },
      minWidth: 60,
    },
    {
      headerName: "Tap Time",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data.StartTap, "HH:mm"),
      minWidth: 65,
    },
    {
      headerName: "TTT",
      valueGetter: (params) => {
        return params.data?.TapToTap?.toFixed(1) || "";
      },
      cellStyle: (params) => {
        const tttValue = params.data?.TapToTap;
        if (tttValue && tttValue > targetTTT) {
          return { color: "red" };
        }
        return null;
      },
      minWidth: 65,
    },
    {
      headerName: "LMF Arrive",
      flex: 1,
      minWidth: 80,
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/lmf-data-entry/${params.data.ProcessId}`,
        });
      },
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.LMFArrive, "HH:mm") || "",
    },
    {
      headerName: "LMF Ship",
      flex: 1,
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.LMFShip, "HH:mm") || "",
      minWidth: 70,
    },
    {
      field: "LastProbeTemperature",
      headerName: "LMF Last Temp",
      valueGetter: (params) => {
        return params.data?.LastProbeTemperature
          ? params.data.LastProbeTemperature.toFixed(0)
          : "";
      },
      minWidth: 110,
    },
    {
      headerName: "Ladle Open",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data.LadleOpen, "HH:mm"),
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/caster-data-entry/${params.data.CASProcessId}`,
        });
      },
      minWidth: 80,
    },
    {
      headerName: "Ladle Close",
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data.LadleClose, "HH:mm"),
      minWidth: 85,
    },
    {
      headerName: "Steel Weight",
      valueGetter: (params) => {
        return params.data?.SteelWeight
          ? params.data.SteelWeight.toFixed(1)
          : "";
      },
      minWidth: 90,
    },
    {
      headerName: "",
      minWidth: 280,
      field: "links",
      cellRenderer: (params: ICellRendererParams) => {
        const isEAFProcessDisabled = params.data.EAFProcessId === null;
        const isLMFProcessDisabled = params.data.LMFProcessId === null;
        const isCASProcessDisabled = params.data.CASProcessId === null;
        return (
          <div className="ag-grid-buttons">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Grid item xs={3} md={3}>
                <Button
                  fullWidth
                  title="See in EAF Data Entry"
                  disabled={isEAFProcessDisabled}
                  aria-label="EAF Page"
                  onClick={() => goToEAFDataEntry(params.data.EAFProcessId)}
                >
                  <span
                    className="material-icons"
                    style={{
                      color: isEAFProcessDisabled ? "#bababa" : "#3f51b5",
                    }}
                  >
                    format_color_fill
                  </span>
                </Button>
              </Grid>
              <Grid item xs={3} md={3}>
                <Button
                  fullWidth
                  title="See in LMF Data Entry"
                  disabled={isLMFProcessDisabled}
                  aria-label="LMF Page"
                  onClick={() => goToLMFDataEntry(params.data.LMFProcessId)}
                >
                  <span
                    className="material-icons"
                    style={{
                      color: isLMFProcessDisabled ? "#bababa" : "#3f51b5",
                    }}
                  >
                    thermostat
                  </span>
                </Button>
              </Grid>
              <Grid item xs={3} md={3}>
                <Button
                  fullWidth
                  title="See in Caster Data Entry"
                  disabled={isCASProcessDisabled}
                  aria-label="Caster Data Entry"
                  onClick={() => goToCasterDataEntry(params.data.CASProcessId)}
                >
                  <span
                    className="material-icons"
                    style={{
                      color: isCASProcessDisabled ? "#bababa" : "#3f51b5",
                    }}
                  >
                    memory
                  </span>
                </Button>
              </Grid>
              <Grid item xs={3} md={3}>
                <Button
                  fullWidth
                  title="See in LAB Data Entry"
                  disabled={isEAFProcessDisabled}
                  aria-label="LAB Page"
                  onClick={() => goToLABDataEntry(params.data.EAFProcessId)}
                >
                  <span
                    className="material-icons"
                    style={{
                      color: isEAFProcessDisabled ? "#bababa" : "#3f51b5",
                    }}
                  >
                    science
                  </span>
                </Button>
              </Grid>
            </Grid>
          </div>
        );
      },
    },

    {
      field: "Actions",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className="ag-grid-buttons">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={1}
              style={{ padding: "0" }}
            >
              <Grid item md={6} xs={6} style={{ padding: "0" }}>
                <IconButton
                  aria-label="Options"
                  title="Options"
                  style={{ cursor: "pointer" }}
                  onClick={(e) =>
                    showContextMenu(e, params.data, "heatsByShiftTable")
                  }
                >
                  <ListIcon
                    fontSize="inherit"
                    color="primary"
                    style={{ cursor: "pointer" }}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </div>
        );
      },
      headerName: " ",
      minWidth: 70,
      sortable: false,
    },
  ];

  return { columns };
};
